import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CommunicationStrategyConfiguration } from '../data/model';
import { StatusHelperService } from '../services/helpers/status-helper.service';
import { FormArray, FormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { faPlus, faSave, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import { CommunicationStrategyService } from '../services/communications-strategy.service';
import { CommunicationsService } from '../services/communications.service';
import { ResponseHelperService } from '../services/helpers/response-helper.service';


@Component({
  selector: 'app-communication-strategy-configuration-modal',
  templateUrl: './communication-strategy-configuration-modal.component.html',
  styleUrls: ['./communication-strategy-configuration-modal.component.scss']
})
export class CommunicationStrategyConfigurationModalComponent implements OnInit {

  @Output() public successEvent: EventEmitter<any> = new EventEmitter();
  @Input() public communicationStrategyConfiguration: CommunicationStrategyConfiguration;
  @Input() public commStrategyId: number;
  endpointTypes: any[] = [];
  title: string;
  isEdit: boolean;
  communicationStrategyConfigurationForm: UntypedFormGroup;
  errorMsg: string;
  faSave = faSave;
  faTimes = faTimes;
  faPlus = faPlus;
  faTrash = faTrash;
  optInOptOutOptions = [{ value: true, text: 'Opt-In' }, { value: false, text: 'Opt-Out' }];
  period = ['DAY', 'WEEK', 'MONTH', 'YEAR', 'CAMPAIGN'];
  days = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
  timeOptions: any[] = [];


  constructor(private formBuilder: FormBuilder,
              private responseHelperService: ResponseHelperService,
              private statusHelperService: StatusHelperService,
              private communicationStrategyService: CommunicationStrategyService,
              private communicationsService: CommunicationsService,
              private modalHelper: NgbModal) { }

  statuses = this.statusHelperService.getStatus('dropdown');

  ngOnInit(): void {
    // 96 = 4 * 24. 4 options for every hour
    const offset = new Date().getTimezoneOffset();
    for (let i = 0; i < 96; i++) {
      const hours = Math.floor(i / 4);
      const minutes = (i % 4) * 15;
      this.timeOptions.push({
        value: this.formatDigit(hours) + ':' + this.formatDigit(minutes) + ':00',
        label: this.formatTime(hours, minutes),
      });
    }
    if (this.communicationStrategyConfiguration) {
      this.title = 'Edit Configuration';
      this.isEdit = true;
      this.communicationStrategyConfigurationForm = this.formBuilder.group({
        fromTime: [this.communicationStrategyConfiguration.fromTime, Validators.required],
        toTime: [this.communicationStrategyConfiguration.toTime, Validators.required],
        optInOptOut: [this.communicationStrategyConfiguration.optInOptOut, Validators.required],
        endpointTypeName: [this.communicationStrategyConfiguration.endpointTypeName, Validators.required],
        blackOutPeriods: [this.communicationStrategyConfiguration.blackOutPeriods &&
          this.communicationStrategyConfiguration.blackOutPeriods.split(',')],
        commCaps: this.formBuilder.array([]),
      });
      if (this.communicationStrategyConfiguration.commCaps) {
        this.communicationStrategyConfiguration.commCaps.forEach((commCap: any) => {
          this.addCommCap(commCap.maxCommunicationsNum, commCap.maxCommunicationsPeriod, commCap.id);
        });
      }
    } else {
      this.title = 'New Configuration';
      this.isEdit = false;
      this.communicationStrategyConfigurationForm = this.formBuilder.group({
        fromTime: [null, Validators.required],
        toTime: [null, Validators.required],
        optInOptOut: [null, Validators.required],
        endpointTypeName: [null, Validators.required],
        blackOutPeriods: [],
        commCaps: this.formBuilder.array([]),
      });
    }
    this.communicationsService.getAllEndpointTypes().subscribe((endpointTypes: any) => {
      if (endpointTypes.success) {
        this.endpointTypes = endpointTypes.entity;
      }
    });
  }

  ok(): void {
    const formValue = Object.assign({}, this.communicationStrategyConfigurationForm.value);
    if (formValue.blackOutPeriods) {
      formValue.blackOutPeriods = formValue.blackOutPeriods.join(',');
    }
    formValue.commStrategyId = this.commStrategyId;
    this.communicationStrategyService.createOrUpdateCommunicationStrategyConfiguration
      (this.communicationStrategyConfiguration && this.communicationStrategyConfiguration.id, formValue).subscribe(() => {
        this.successEvent.emit('success');
      }, (data: any) => {
        this.responseHelperService.error(this, data.error.error, false);
      });
  }

  removeCommCap(i: number): void {
    this.getCommCaps().removeAt(i);
  }

  addCommCap(maxCommunicationsNum?: string, maxCommunicationsPeriod?: string, id?: number): void {
    this.getCommCaps().push(this.newCommCaps(maxCommunicationsNum, maxCommunicationsPeriod, id));
  }

  newCommCaps(maxCommunicationsNum?: string, maxCommunicationsPeriod?: string, id?: number): UntypedFormGroup {
    return this.formBuilder.group({
      maxCommunicationsNum: [maxCommunicationsNum || '', Validators.required],
      maxCommunicationsPeriod: [maxCommunicationsPeriod || '', Validators.required],
      commStrategyConfigurationId: [this.communicationStrategyConfiguration && this.communicationStrategyConfiguration.id],
      id: [id],
    });
  }

  getCommCaps(): FormArray {
    return this.communicationStrategyConfigurationForm.controls.commCaps as FormArray;
  }

  cancel(): void {
    this.modalHelper.dismissAll();
  }

  formatDigit(digit: number): any {
    return digit > 9 ? digit : '0' + digit;
  }

  convertToMiliSeconds(value: string): number {
    const hours = Number(value.split(':')[0]);
    const minutes = Number(value.split(':')[1]);
    const date = new Date();
    // we're trying to force the user to select a UTC time, hence the tomfoolery below
    return Date.UTC(date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      hours,
      minutes);
  }

  clearErrorMessage(): void {
    this.errorMsg = '';
  }

  formatTime(hours: number, minutes: number): Date {
    const date1 = new Date();
    date1.setHours(hours);
    date1.setMinutes(minutes);
    date1.setSeconds(0);
    return date1;
  }

}
